import { useCallback, useMemo } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { useDocumentViewport } from "@/features/document-viewport/context";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import type { Point } from "@/lib/geometry";
import {
  DraftMeasurementCard,
  MeasurementContextMenu,
  SelectedMeasurementCard,
} from "@/viewport-tools/measurement/overlay/MeasurementOverlayPanels";
import {
  DraftMeasurementPreviewLayer,
  PersistedMeasurementsLayer,
} from "@/viewport-tools/measurement/overlay/MeasurementOverlayLayers";
import { saveMeasurementEdits } from "@/viewport-tools/measurement/overlay/measurement-overlay-edit-actions";
import {
  computeValue,
  getClosestSegmentInsertionPoint,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";
import {
  type DraftMeasurementState,
  MEASUREMENT_GEOMETRY_TYPES,
  type MeasurementAnnotation,
  type MeasurementGeometry,
  type MeasurementOverlayRecord,
  type MeasurementTagOption,
  type PolygonGeometry,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-types";
import {
  type MeasurementType,
  TOOL_IDS,
  type ToolType,
} from "@/viewport-tools/measurement";
import { useMeasurementOverlayInteractions } from "@/viewport-tools/measurement/overlay/useMeasurementOverlayInteractions";
import { toast } from "sonner";

interface MeasurementToolConfig {
  scale: MeasurementScale | null;
  activeTool: ToolType | null;
  activeMeasurementType: MeasurementType | null;
  currentHeightFeet?: number;
  snapEnabled: boolean;
  incrementSnapEnabled: boolean;
  incrementStepFeet: number;
  textLocked?: boolean;
  textFontSize?: number;
}

interface TagDisplayData {
  colorByTagId: Map<string, string>;
  labelByTagId: Map<string, string>;
  tags: MeasurementTagOption[];
}

interface DraftMeasurementCallbacks {
  draftMeasurement: DraftMeasurementState | null;
  draftTagId: Id<"takeoffPilotExtractedTags"> | null;
  onDraftMeasurementChange: (draft: DraftMeasurementState | null) => void;
  onDraftTagChange: (tagId: Id<"takeoffPilotExtractedTags">) => void;
  onSaveDraftMeasurement: () => Promise<void>;
  onCancelDraftMeasurement: () => void;
  isSavingDraftMeasurement?: boolean;
}

interface MeasurementSelectionState {
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  activeMeasurementId: Id<"takeoffPilotMeasurements"> | null;
  onSelectMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements"> | null,
    options?: { shiftKey?: boolean }
  ) => void;
  onReassignMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    tagId: Id<"takeoffPilotExtractedTags">
  ) => Promise<boolean>;
  onReassignMeasurements?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    tagId: Id<"takeoffPilotExtractedTags">
  ) => Promise<boolean>;
  isReassigningMeasurementId?: Id<"takeoffPilotMeasurements"> | null;
  isDeletingMeasurementId?: Id<"takeoffPilotMeasurements"> | null;
  onDeleteMeasurement: (measurementId: Id<"takeoffPilotMeasurements">) => Promise<void>;
  onDeleteMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<void>;
  onApplyMeasurementLeaders?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    leaderVisible: boolean
  ) => Promise<boolean>;
}

interface MeasurementCrudOperations {
  measurements: MeasurementOverlayRecord[];
  onUpdateMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    geometry: MeasurementGeometry | PolygonGeometry,
    height: number | undefined,
    value: number
  ) => Promise<void>;
  onUpdateMeasurementAnnotation?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation
  ) => Promise<void>;
}

interface ClipboardOperations {
  canPasteMeasurement?: boolean;
  onCopyMeasurement?: (measurementId: Id<"takeoffPilotMeasurements">) => void;
  onCopyMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => void;
  onPasteMeasurement?: (point?: Point) => Promise<void> | void;
  onDuplicateMeasurement?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    point?: Point
  ) => Promise<void> | void;
  onDuplicateMeasurements?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    point?: Point
  ) => Promise<void> | void;
}

interface MeasurementOverlayProps
  extends MeasurementToolConfig,
    TagDisplayData,
    DraftMeasurementCallbacks,
    MeasurementSelectionState,
    MeasurementCrudOperations,
    ClipboardOperations {}

export function MeasurementOverlay({
  scale,
  activeTool,
  activeMeasurementType,
  currentHeightFeet,
  snapEnabled,
  incrementSnapEnabled,
  incrementStepFeet,
  textLocked = false,
  textFontSize = 13,
  measurements,
  colorByTagId,
  labelByTagId,
  draftMeasurement,
  draftTagId,
  tags,
  onDraftMeasurementChange,
  onDraftTagChange,
  onSaveDraftMeasurement,
  onCancelDraftMeasurement,
  isSavingDraftMeasurement = false,
  selectedMeasurementIds,
  activeMeasurementId,
  onSelectMeasurement,
  onUpdateMeasurement,
  onUpdateMeasurementAnnotation,
  onReassignMeasurement,
  onReassignMeasurements,
  isReassigningMeasurementId = null,
  isDeletingMeasurementId = null,
  onDeleteMeasurement,
  onDeleteMeasurements,
  onApplyMeasurementLeaders,
  canPasteMeasurement = false,
  onCopyMeasurement,
  onCopyMeasurements,
  onPasteMeasurement,
  onDuplicateMeasurement,
  onDuplicateMeasurements,
}: MeasurementOverlayProps) {
  const viewport = useDocumentViewport();
  const {
    overlayRef,
    interaction,
    drawingDraft,
    previewAnnotations,
    previewGeometries,
    errorMessage,
    contextMenu,
    latestDraftMeasurement,
    handleCancelDraft,
    handleSaveDraft,
    handleStageMouseDown,
    handleStageContextMenu,
    handleStageMouseMove,
    handleStageMouseUp,
    handleStageMouseLeave,
    startDraftMove,
    openContextMenu,
    startMeasurementMove,
    startMeasurementResize,
    startMeasurementVertexDrag,
    openVertexContextMenu,
    startMeasurementLabelMove,
    closeContextMenu,
  } = useMeasurementOverlayInteractions({
    viewport,
    scale,
    activeTool,
    activeMeasurementType,
    currentHeightFeet,
    snapEnabled,
    incrementSnapEnabled,
    incrementStepFeet,
    measurements,
    draftMeasurement,
    selectedMeasurementIds,
    activeMeasurementId,
    canPasteMeasurement,
    onDraftMeasurementChange,
    onSaveDraftMeasurement,
    onCancelDraftMeasurement,
    onSelectMeasurement,
    onUpdateMeasurement,
    onUpdateMeasurementAnnotation,
    onCopyMeasurement,
    onCopyMeasurements,
    onPasteMeasurement,
    onDuplicateMeasurement,
    onDuplicateMeasurements,
  });

  const handleSaveMeasurementEdits = useCallback(
    async (
      measurementId: Id<"takeoffPilotMeasurements">,
      edits: { valueInput: string; valueChanged: boolean; heightInput: string; heightChanged: boolean }
    ) => {
      await saveMeasurementEdits({
        measurementId,
        edits,
        measurements,
        scale,
        onUpdateMeasurement,
      });
    },
    [measurements, onUpdateMeasurement, scale]
  );

  const handleInsertMeasurementPoint = useCallback(
    async (measurementId: Id<"takeoffPilotMeasurements">, pagePoint: Point) => {
      const measurement = measurements.find((item) => item._id === measurementId);
      if (
        !measurement ||
        measurement.geometry.type !== MEASUREMENT_GEOMETRY_TYPES.poly ||
        (measurement.tool !== TOOL_IDS.polyline && measurement.tool !== TOOL_IDS.polygon)
      ) {
        return;
      }

      const isClosed = measurement.tool === TOOL_IDS.polygon;
      const insertion = getClosestSegmentInsertionPoint(
        measurement.geometry.vertices,
        pagePoint,
        isClosed
      );
      if (!insertion) {
        return;
      }

      const nextVertices = [...measurement.geometry.vertices];
      nextVertices.splice(insertion.segmentIndex + 1, 0, insertion.point);
      const nextGeometry: PolygonGeometry = {
        type: MEASUREMENT_GEOMETRY_TYPES.poly,
        vertices: nextVertices,
      };
      const nextValue = scale
        ? computeValue(
            measurement.tool,
            measurement.measurementType,
            nextGeometry,
            measurement.height,
            scale.pixelsPerFoot,
            scale.pageAspectRatio
          )
        : measurement.value;

      try {
        await onUpdateMeasurement(measurement._id, nextGeometry, measurement.height, nextValue);
      } catch (error) {
        console.error("Failed to insert measurement point:", error);
        toast.error("Failed to insert point");
      }
    },
    [measurements, onUpdateMeasurement, scale]
  );

  const handleRemoveMeasurementPoint = useCallback(
    async (measurementId: Id<"takeoffPilotMeasurements">, vertexIndex: number) => {
      const measurement = measurements.find((item) => item._id === measurementId);
      if (
        !measurement ||
        measurement.geometry.type !== MEASUREMENT_GEOMETRY_TYPES.poly ||
        (measurement.tool !== TOOL_IDS.polyline && measurement.tool !== TOOL_IDS.polygon)
      ) {
        return;
      }

      const minVertexCount = measurement.tool === TOOL_IDS.polygon ? 3 : 2;
      if (
        vertexIndex < 0 ||
        vertexIndex >= measurement.geometry.vertices.length ||
        measurement.geometry.vertices.length <= minVertexCount
      ) {
        return;
      }

      const nextGeometry: PolygonGeometry = {
        type: MEASUREMENT_GEOMETRY_TYPES.poly,
        vertices: measurement.geometry.vertices.filter((_, index) => index !== vertexIndex),
      };
      const nextValue = scale
        ? computeValue(
            measurement.tool,
            measurement.measurementType,
            nextGeometry,
            measurement.height,
            scale.pixelsPerFoot,
            scale.pageAspectRatio
          )
        : measurement.value;

      try {
        await onUpdateMeasurement(measurement._id, nextGeometry, measurement.height, nextValue);
      } catch (error) {
        console.error("Failed to remove measurement point:", error);
        toast.error("Failed to remove point");
      }
    },
    [measurements, onUpdateMeasurement, scale]
  );

  const selectedMeasurements = useMemo(() => {
    if (selectedMeasurementIds.length <= 1) {
      return [];
    }
    return measurements.filter((measurement) => selectedMeasurementIds.includes(measurement._id));
  }, [measurements, selectedMeasurementIds]);

  const contextMeasurementId =
    contextMenu?.target.type === "measurement" || contextMenu?.target.type === "measurement-vertex"
      ? contextMenu.target.measurementId
      : null;

  return (
    <div
      ref={overlayRef}
      className={`absolute inset-0 z-30 ${
        activeTool || interaction || selectedMeasurementIds.length > 0 || canPasteMeasurement
          ? "pointer-events-auto"
          : "pointer-events-none"
      }`}
      onMouseDown={handleStageMouseDown}
      onContextMenu={handleStageContextMenu}
      onMouseMove={handleStageMouseMove}
      onMouseUp={() => void handleStageMouseUp()}
      onMouseLeave={handleStageMouseLeave}
    >
      <PersistedMeasurementsLayer
        viewport={viewport}
        measurements={measurements}
        selectedMeasurementIds={selectedMeasurementIds}
        previewAnnotations={previewAnnotations}
        previewGeometries={previewGeometries}
        colorByTagId={colorByTagId}
        labelByTagId={labelByTagId}
        scale={scale}
        textLocked={textLocked}
        textFontSize={textFontSize}
        startMeasurementMove={startMeasurementMove}
        startMeasurementResize={startMeasurementResize}
        startMeasurementVertexDrag={startMeasurementVertexDrag}
        openVertexContextMenu={openVertexContextMenu}
        startMeasurementLabelMove={startMeasurementLabelMove}
        openContextMenu={openContextMenu}
      />

      <DraftMeasurementPreviewLayer
        viewport={viewport}
        drawingDraft={drawingDraft}
        draftMeasurement={draftMeasurement}
        interaction={interaction}
        startDraftMove={startDraftMove}
      />

      {errorMessage ? (
        <div className="absolute top-16 left-1/2 z-90 -translate-x-1/2 rounded-lg border border-red-500 bg-background/95 px-4 py-2 shadow-xl">
          <div className="text-sm text-red-500">{errorMessage}</div>
        </div>
      ) : null}

      {(draftMeasurement || (!interaction && latestDraftMeasurement && !drawingDraft)) && (
        <DraftMeasurementCard
          draft={draftMeasurement ?? latestDraftMeasurement!}
          draftTagId={draftTagId}
          tags={tags}
          onDraftTagChange={onDraftTagChange}
          onSaveDraftMeasurement={handleSaveDraft}
          onCancelDraftMeasurement={handleCancelDraft}
          isSavingDraftMeasurement={isSavingDraftMeasurement}
        />
      )}

      {activeMeasurementId ? (
        <SelectedMeasurementCard
          measurement={measurements.find((m) => m._id === activeMeasurementId) ?? null}
          selectedMeasurements={selectedMeasurements}
          tags={tags}
          scale={scale}
          onSaveMeasurementEdits={handleSaveMeasurementEdits}
          onUpdateMeasurementAnnotation={onUpdateMeasurementAnnotation}
          onReassignMeasurement={onReassignMeasurement}
          onReassignMeasurements={onReassignMeasurements}
          isReassigningMeasurementId={isReassigningMeasurementId}
          isDeletingMeasurementId={isDeletingMeasurementId}
          onDeleteMeasurement={onDeleteMeasurement}
          onDeleteMeasurements={onDeleteMeasurements}
          onApplyMeasurementLeaders={onApplyMeasurementLeaders}
          onCopyMeasurements={onCopyMeasurements}
          onDuplicateMeasurements={onDuplicateMeasurements}
        />
      ) : null}

      {contextMenu ? (
        <MeasurementContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          measurement={
            contextMeasurementId
              ? measurements.find((measurement) => measurement._id === contextMeasurementId) ?? null
              : null
          }
          pagePoint={contextMenu.pagePoint}
          vertexIndex={
            contextMenu.target.type === "measurement-vertex"
              ? contextMenu.target.vertexIndex
              : undefined
          }
          canPasteMeasurement={canPasteMeasurement}
          selectedMeasurementIds={selectedMeasurementIds}
          onClose={closeContextMenu}
          onCopyMeasurement={onCopyMeasurement}
          onCopyMeasurements={onCopyMeasurements}
          onPasteMeasurement={() => onPasteMeasurement?.(contextMenu.pagePoint ?? undefined)}
          onDuplicateMeasurement={(measurementId) =>
            onDuplicateMeasurement?.(measurementId, contextMenu.pagePoint ?? undefined)
          }
          onDuplicateMeasurements={(measurementIds) =>
            onDuplicateMeasurements?.(measurementIds, contextMenu.pagePoint ?? undefined)
          }
          measurements={measurements}
          onApplyMeasurementLeaders={onApplyMeasurementLeaders}
          onInsertPoint={handleInsertMeasurementPoint}
          onRemovePoint={handleRemoveMeasurementPoint}
        />
      ) : null}
    </div>
  );
}

export type { MeasurementOverlayProps };
